import { t } from 'i18next';
import { Arbeitszeit } from '../../../../../entities';
import { useArbeitszeitUpdate } from '../../../../../hooks';
import { useState } from 'react';
import { handleOnCompleted } from '../../../handleOnCompleted';
import { ArbeitszeitenPauseneinstellung } from '../../../../../../../gql/graphql';
import { Label, Switch } from '@e2n/ui';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { useManagerCanEditArbeitszeiten } from '@/domain/arbeitszeiten/hooks/useManagerCanEditArbeitszeit';

// eslint-disable-next-line max-lines-per-function
export function PausenEinstellungen({
  arbeitszeit,
}: {
  arbeitszeit: Arbeitszeit;
}) {
  const { pauseneinstellung, id: arbeitszeitId } = arbeitszeit;
  const [arbeitszeitUpdate] = useArbeitszeitUpdate();
  const canEditArbeitszeit = useManagerCanEditArbeitszeiten();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isGesetzlich, setIsGesetzlich] = useState(
    pauseneinstellung === ArbeitszeitenPauseneinstellung.Gesetzlich,
  );
  const [isAutomatisch, setIsAutomatisch] = useState(
    pauseneinstellung === ArbeitszeitenPauseneinstellung.Automatisch,
  );

  async function onChangePauseneinstellung(
    isAutomatischChecked: boolean,
    isGesetzlichChecked: boolean,
  ) {
    let pauseneinstellung = ArbeitszeitenPauseneinstellung.Ohne;
    if (isAutomatischChecked && !isGesetzlichChecked) {
      pauseneinstellung = ArbeitszeitenPauseneinstellung.Automatisch;
    }
    if (isGesetzlichChecked && !isAutomatischChecked) {
      pauseneinstellung = ArbeitszeitenPauseneinstellung.Gesetzlich;
    }
    setIsUpdating(true);
    arbeitszeitUpdate({
      arbeitszeitUpdateInput: {
        pauseneinstellung,
      },
      arbeitszeitId,
    })
      .then((result) => {
        if (!result.data) {
          setIsUpdating(false);
          return;
        }
        handleOnCompleted(result.data, t);
        setIsUpdating(false);
      })
      .catch((e) => {
        toast({
          variant: 'destructive',
          title: t(
            'domain.arbeitszeiten.sheet.header.arbeitszeitUpdateUnknownError',
          ),
        });
        Sentry.captureException(e);
        setIsUpdating(false);
      });
  }

  return (
    <div className="flex gap-4 items-center">
      {isUpdating && (
        <div className="text-xs">{t('actions.wirdGespeichert')}</div>
      )}
      <div className="flex items-center space-x-2">
        <Switch
          id="pauseneinstellung-gesetzlich"
          size="sm"
          checked={isGesetzlich}
          disabled={isUpdating || !canEditArbeitszeit}
          onCheckedChange={(isGesetzlich) => {
            setIsGesetzlich(isGesetzlich);
            if (isGesetzlich && isAutomatisch) {
              setIsAutomatisch(false);
              onChangePauseneinstellung(false, true);
            } else if (!isGesetzlich && !isAutomatisch) {
              onChangePauseneinstellung(false, false);
            } else {
              onChangePauseneinstellung(false, true);
            }
          }}
        />
        <Label htmlFor="pauseneinstellung-gesetzlich">
          {t(
            'domain.arbeitszeiten.sheet.arbeitszeit.pauseneinstellungenGesetzlich',
          )}
        </Label>
      </div>
      <div className="flex items-center space-x-2">
        <Switch
          id="pauseneinstellung-automatisch"
          size="sm"
          disabled={isUpdating || !canEditArbeitszeit}
          checked={isAutomatisch}
          onCheckedChange={(isAutomatisch) => {
            setIsAutomatisch(isAutomatisch);
            if (isAutomatisch && isGesetzlich) {
              setIsGesetzlich(false);
              onChangePauseneinstellung(true, false);
            } else if (!isAutomatisch && !isAutomatisch) {
              onChangePauseneinstellung(false, false);
            } else {
              onChangePauseneinstellung(true, false);
            }
          }}
        />
        <Label htmlFor="pauseneinstellung-automatisch">
          {t(
            'domain.arbeitszeiten.sheet.arbeitszeit.pauseneinstellungenAutomatisch',
          )}
        </Label>
      </div>
    </div>
  );
}
